<template>
  <div id="app" v-if="(windowWidth >= LARGE_SCREEN_SIZE || route != EMPLOYEE_PAYROLL_DETAILS_ROUTE) ? true : false">
    <div v-if="routerIsLoading" class="spinner-overlay">
      <div class="spinner"></div>
    </div>
    <router-view />
  </div>
  <div v-else class="container">
    <br />
    <div class="alert alert-secondary" role="alert">
      <h6 class="text-center">
        Content can not be displayed on small screens. Please use a larger
        screen.
      </h6>
    </div>
  </div>
</template>
<script>
import {
  LARGE_SCREEN_SIZE,
  DEFAULT_ERROR_MSG,
  TOAST_GLOBAL_CONFIG,
} from "@/utils/const";
import { CLEAR_ERRORMSG, CLEAR_CHAT } from "@/store/action_types";
import { connectChatSocket, disconnectChatSocket, nudgeChatSocket } from "@/utils/chat_socket";
import { PlatformModule } from "@/utils/enums";
import router from "@/router";

export default {
  data() {
    return {
      isLoading: false,
      LARGE_SCREEN_SIZE,
      windowWidth: 0,
    };
  },
  watch: {
    sessionToken() {
      this.syncChatSocket();
    },
    organizationId() {
      this.syncChatSocket();
    },
    chatEnabled() {
      this.syncChatSocket();
    },
    errorMessage: {
      handler(val) {
        if (val != null) {
          this.$toast.error(val ?? DEFAULT_ERROR_MSG, TOAST_GLOBAL_CONFIG);
          this.$store.dispatch(CLEAR_ERRORMSG);
        }
      },
    },
  },
  computed: {
    route() {
      return this.$route.name;
    },
    sessionToken() {
      return this.$store.getters.sessionToken;
    },
    organizationId() {
      return this.$store.getters.account.data?.organizationId ?? null;
    },
    /** Chat is a licensed module; without it there is nothing to connect to. */
    chatEnabled() {
      return this.$store.getters.enabledModules.includes(PlatformModule.chat);
    },
    routerIsLoading() {
      return this.$store.getters.routerIsLoading;
    },
    errorMessage() {
      return this.$store.getters.errorMsg;
    },
  },
  methods: {
    getScreenSize() {
      this.windowWidth = window.innerWidth;
    },
    /** One live connection while a signed-in member of an organization licensed for chat is here; none otherwise. */
    syncChatSocket() {
      if (this.sessionToken && this.organizationId && this.chatEnabled) {
        connectChatSocket(this.sessionToken);
      } else {
        disconnectChatSocket();
        this.$store.dispatch(CLEAR_CHAT);
      }
    },
    /** The mobile app hands over the page a tapped notification points at; route without a reload. */
    openNativeUrl(url) {
      try {
        const target = new URL(String(url), window.location.origin);
        if (target.origin !== window.location.origin) return false;
        router.push(target.pathname + target.search + target.hash);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
  created() {
    window.addEventListener("resize", () => this.getScreenSize());
    this.getScreenSize();
    this.syncChatSocket();
    window.addEventListener("appResumed", () => nudgeChatSocket());
    window.__abolOpenUrl = (url) => this.openNativeUrl(url);
  },
};
</script>

<style lang="css">
@import "./assets/style/index.css";
@import "./assets/style/sidenav.css";
@import "./assets/style/dashboard.css";
@import "./assets/style/recruitment.css";
@import "./assets/style/reports.css";
@import "./assets/style/settings.css";
@import "./assets/style/export_templates.css";
@import "./assets/style/payroll.css";
@import "./assets/style/employee.css";
@import "./assets/style/leave.css";
@import "./assets/style/overtime.css";
@import "./assets/style/organization.css";
@import "./assets/style/help.css";
@import "./assets/style/chat.css";
@import "./assets/style/files.css";

.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.spinner {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-left-color: #14243d;
  animation: spin 1s linear infinite;
}

.spinner img {
  height: 60px;
  z-index: 10; /* Ensures the image is above the spinner */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
